import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ClientDetailsFetchRequest,
  ClientDetailsGetPopiaStatusRequest,
  ClientDetailsGetSearchPartyRequest,
  ClientDetailsSubmitRequest,
  ClientDetailsUpdatePopiaStatusRequest,
  ClientDetailsUpdateRequest,
  ResponseError,
  Result,
  ResultStates,
  useInvalidateIndividualDetails,
  useInvalidateProductOptions,
  useInvalidateProposalInformation
} from 'bff';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { minutesToMilliseconds } from 'date-fns';
import { clientService } from 'stores/client-store/client-store';
import { DeepNullableOfOptionalFields } from 'types/form/DeepNullableOfOptionalFields.type';
import { useInvalidateCurrentProducts } from './ProductsSelectionApi';
import { useInvalidateProposalSummaryDetails } from './ProposalSummaryApi';
import { useInvalidateCompanyDetails } from '../applications/personal-details/CompanyDetailsApi';
import { useInvalidateTrustDetails } from '../applications/personal-details/TrustDetailsApi';
import { useInvalidateNonTaxableInstitutionDetails } from '../applications/personal-details/NonTaxableInstitutionDetailsApi';

export const clientDetailsKeys = {
  base: () => ['client-details'] as const,
  clientDetails: (clientDetailsRequest: ClientDetailsFetchRequest) =>
    [...clientDetailsKeys.base(), clientDetailsRequest] as const,
  search: (searchPartyRequest: ClientDetailsGetSearchPartyRequest) =>
    [...clientDetailsKeys.base(), searchPartyRequest] as const
};

export const useInvalidateClientDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: clientDetailsKeys.base(),
      refetchType: 'all'
    });
};

export const getClientDetailsOptions = (clientDetailsRequest: ClientDetailsFetchRequest) => {
  const { clientDetailsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: clientDetailsKeys.clientDetails(clientDetailsRequest),
    queryFn: () => clientDetailsApi.clientDetailsFetch(clientDetailsRequest),
    staleTime: minutesToMilliseconds(5),
    select: (response) => response.data!
  });
};

export const getClientPopiaStatus = (clientDetailsRequest: ClientDetailsGetPopiaStatusRequest) => {
  const { clientDetailsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: ['clientPopiaStatus', clientDetailsRequest.partyId, clientDetailsRequest.proposalId],
    queryFn: () => clientDetailsApi.clientDetailsGetPopiaStatus(clientDetailsRequest),
    staleTime: 0,
    gcTime: 0,
    select: (response) => {
      if (response.data) {
        return { ...response.data };
      }
    }
  });
};

export const useSubmitClientDetails = () => {
  const { clientDetailsApi } = clientService.getApiClient();
  const invalidateProductOptions = useInvalidateProductOptions();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateCurrentProducts = useInvalidateCurrentProducts();
  const invalidateProposalSummary = useInvalidateProposalSummaryDetails();

  return useMutation({
    mutationFn: (clientDetails: ClientDetailsSubmitRequest) =>
      clientDetailsApi.clientDetailsSubmit(clientDetails),
    onSuccess: async () => {
      await invalidateProposalInformation();
      await invalidateProductOptions();
      await invalidateCurrentProducts();
      await invalidateProposalSummary();
    }
  });
};

export const getClientDetailsSearchPartyOptions = (
  clientDetailsGetSearchPartyRequest: ClientDetailsGetSearchPartyRequest
) => {
  const { clientDetailsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: clientDetailsKeys.search(clientDetailsGetSearchPartyRequest),
    queryFn: () => clientDetailsApi.clientDetailsGetSearchParty(clientDetailsGetSearchPartyRequest),
    staleTime: minutesToMilliseconds(5),
    select: (response) => response.data!,
    retry: (failureCount, error: ResponseError) => {
      return error.response.status === 404 ? false : failureCount < 3;
    }
  });
};

export const updateClientsMutateKey = ['update-client-details'];

export const useUpdateClientDetails = (
  options?: UseDisplayErrorMutationOptions<
    Result,
    DeepNullableOfOptionalFields<ClientDetailsUpdateRequest>
  >
) => {
  const { clientDetailsApi } = clientService.getApiClient();
  const queryClient = useQueryClient();
  const invalidateProductOptions = useInvalidateProductOptions();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateCurrentProducts = useInvalidateCurrentProducts();
  const invalidateProposalSummary = useInvalidateProposalSummaryDetails();
  const invalidateIndividualDetails = useInvalidateIndividualDetails();
  const invalidateCompanyDetails = useInvalidateCompanyDetails();
  const invalidateTrustDetails = useInvalidateTrustDetails();
  const invalidateNonTaxableInstitutionDetails = useInvalidateNonTaxableInstitutionDetails();

  return useMutation({
    ...options,
    mutationKey: updateClientsMutateKey,
    mutationFn: (clientDetails: DeepNullableOfOptionalFields<ClientDetailsUpdateRequest>) =>
      clientDetailsApi.clientDetailsUpdate(clientDetails as ClientDetailsUpdateRequest),
    onSuccess: async (...props) => {
      if (props[0].resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(props[0]);
      } else {
        options?.onSuccess?.(...props);

        await Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: clientDetailsKeys.base() }),
          invalidateProposalInformation(),
          invalidateProductOptions(),
          invalidateCurrentProducts(),
          invalidateProposalSummary(),
          invalidateIndividualDetails(),
          invalidateCompanyDetails(),
          invalidateTrustDetails(),
          invalidateNonTaxableInstitutionDetails()
        ]);
      }
    }
  });
};

export const updateClientPopiaMutateKey = ['update-client-popia-status'];

export const useUpdatePopiaStatus = (
  options?: UseDisplayErrorMutationOptions<
    Result,
    DeepNullableOfOptionalFields<ClientDetailsUpdatePopiaStatusRequest>
  >
) => {
  const { clientDetailsApi } = clientService.getApiClient();

  return useMutation({
    ...options,
    mutationKey: [updateClientPopiaMutateKey],
    mutationFn: async (request: ClientDetailsUpdatePopiaStatusRequest) => {
      return await clientDetailsApi.clientDetailsUpdatePopiaStatus({
        partyId: request.partyId,
        updateModel: {
          popiaStatus: request.updateModel.popiaStatus,
          detailsLastUpdated: request.updateModel.detailsLastUpdated
        }
      });
    }
  });
};
